import type { WorkItem } from './workflow'

const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000

// The per-phase "Done" column: a projection of the work item's phase ledger,
// the operational record the backend engine writes on every movement. History
// is audit-only and is never consulted here.
//
// A task belongs to a phase's Done column when it has a concluded visit to
// that phase: it was handed onward (and has not come back), or it was
// completed while in that phase (the final phase's case). An item reworked in
// the phase right now is a live card, not a Done entry.
const phaseDoneLedger = (phaseId: string, workItems: WorkItem[]): WorkItem[] =>
	workItems.filter(item => {
		const concluded = item.phaseLedger.some(
			entry =>
				entry.phaseId === phaseId &&
				entry.leftAt !== null &&
				(entry.outcome === 'handed_off' || entry.outcome === 'completed'),
		)
		if (!concluded) return false
		const activeHere = item.currentPhaseId === phaseId && item.status !== 'completed'
		return !activeHere
	})

// Returns true when the item arrived at its current phase via a prior phase
// visit - i.e. there is a sender phase to reject back to.
const hasSenderPhase = (item: WorkItem): boolean => item.phaseLedger.length > 1

export { phaseDoneLedger, hasSenderPhase }

// Global "Archived" ledger: a live projection of all concluded tasks on the
// board. The age rule applies ONLY to completed tasks - cancelled and archived
// items appear unconditionally. A completed task whose completion timestamp is
// older than one year moves here so it no longer clutters the Done ledger.
// The `nowMs` reference is passed in (never read from the clock here) to keep
// the function pure and testable.
const workflowArchivedLedger = (workItems: WorkItem[], nowMs: number): WorkItem[] =>
	workItems.filter(item => {
		if (item.status === 'archived' || item.status === 'cancelled') return true
		if (item.status !== 'completed' || !item.completedAt) return false
		return nowMs - new Date(item.completedAt).getTime() >= ONE_YEAR_MS
	})

export { workflowArchivedLedger }
